import React, { useEffect, useState } from "react";
import {
  Modal,
  Form,
  Input,
  InputNumber,
  DatePicker,
  Select,
  Button,
  Row,
  Col,
  message,
} from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import {
  AptEvent,
  AptEventType,
  AptEventExtra,
  ApartmentCurrency,
  ClientApartment,
  createAptEvent,
  updateAptEvent,
} from "../../../utils/api/apartmentRentalsAPI";

const { TextArea } = Input;
const { Option } = Select;

interface Props {
  open: boolean;
  onClose: () => void;
  onSaved: (ev: AptEvent) => void;
  apartments: ClientApartment[];
  editData?: AptEvent | null;
  clientId: string;
}

const DEFAULT_EXTRAS: AptEventExtra[] = [];

const AddEventModal: React.FC<Props> = ({
  open,
  onClose,
  onSaved,
  apartments,
  editData,
  clientId,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [eventType, setEventType] = useState<AptEventType>("blogger");
  const [currency, setCurrency] = useState<ApartmentCurrency>("RSD");
  const [extras, setExtras] = useState<AptEventExtra[]>(DEFAULT_EXTRAS);

  const isEdit = !!editData;

  useEffect(() => {
    if (!open) return;

    if (editData) {
      const aptId =
        typeof editData.apartmentId === "object"
          ? (editData.apartmentId as any)._id
          : editData.apartmentId;

      form.setFieldsValue({
        title: editData.title,
        sub: editData.sub,
        apartmentId: aptId,
        startDate: editData.startDate ? dayjs(editData.startDate) : undefined,
        endDate: editData.endDate ? dayjs(editData.endDate) : undefined,
        nights: editData.nights,
        cost: editData.cost,
        extraCost: editData.extraCost,
        guestPayment: editData.guestPayment,
        deliverables: editData.deliverables,
        contact: editData.contact,
      });
      setEventType(editData.type);
      setCurrency(editData.currency || "RSD");
      setExtras(editData.extras || []);
    } else {
      form.resetFields();
      form.setFieldsValue({ nights: 1, cost: 0, extraCost: 0, guestPayment: 0 });
      setEventType("blogger");
      setCurrency("RSD");
      setExtras([]);
    }
  }, [open, editData, form]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const aptId = values.apartmentId;
      const aptName = apartments.find((a) => a._id === aptId)?.name;

      const payload: Partial<AptEvent> = {
        type: eventType,
        title: values.title,
        sub: values.sub,
        apartmentId: aptId,
        apartmentName: aptName,
        currency,
        startDate: values.startDate
          ? (values.startDate as dayjs.Dayjs).format("YYYY-MM-DD")
          : undefined,
        endDate: values.endDate
          ? (values.endDate as dayjs.Dayjs).format("YYYY-MM-DD")
          : undefined,
        nights: values.nights || 0,
        cost: values.cost || 0,
        extraCost: values.extraCost || 0,
        guestPayment: values.guestPayment || 0,
        extras,
        deliverables: values.deliverables,
        contact: values.contact,
      };

      let result;
      if (isEdit && editData) {
        result = await updateAptEvent(editData._id, payload);
      } else {
        result = await createAptEvent(clientId, payload);
      }

      onSaved(result.data);
      onClose();
    } catch (err: any) {
      if (!err?.errorFields) {
        message.error("Greška pri čuvanju događaja");
      }
    } finally {
      setLoading(false);
    }
  };

  const addExtra = () =>
    setExtras((prev) => [...prev, { label: "", cls: "" }]);

  const removeExtra = (i: number) =>
    setExtras((prev) => prev.filter((_, idx) => idx !== i));

  const updateExtra = (i: number, field: keyof AptEventExtra, val: string) =>
    setExtras((prev) =>
      prev.map((e, idx) => (idx === i ? { ...e, [field]: val } : e))
    );

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: eventType === "blogger" ? "#ede9fd" : "#fef9c3",
              color: eventType === "blogger" ? "#5b50d6" : "#ca8a04",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 18,
              flexShrink: 0,
            }}
          >
            {eventType === "blogger" ? "👤" : "🏷"}
          </span>
          <div>
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: "#6b6880",
                textTransform: "uppercase",
                letterSpacing: "0.08em",
              }}
            >
              {isEdit ? "Izmijeni događaj" : "Novi događaj"}
            </div>
            <div style={{ fontSize: 15, fontWeight: 600, marginTop: 1 }}>
              {isEdit ? "Uredi detalje" : "Dodaj događaj"}
            </div>
          </div>
        </div>
      }
      width={560}
      destroyOnClose
      footer={
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Button onClick={onClose}>Otkaži</Button>
          <Button
            type="primary"
            loading={loading}
            onClick={handleSave}
            style={{ background: "#1a1520", borderColor: "#1a1520" }}
          >
            {isEdit ? "Sačuvaj izmjene" : "Dodaj događaj"}
          </Button>
        </div>
      }
    >
      <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
        {/* Tip */}
        <Form.Item label="Tip događaja" style={{ marginBottom: 14 }}>
          <div className="ar-seg-pick">
            <button
              type="button"
              className={eventType === "blogger" ? "active" : ""}
              onClick={() => setEventType("blogger")}
            >
              👤 Bloger / Influencer
            </button>
            <button
              type="button"
              className={eventType === "discount" ? "active" : ""}
              onClick={() => setEventType("discount")}
            >
              🏷 Akcija / Popust
            </button>
          </div>
        </Form.Item>

        {/* Naslov */}
        <Row gutter={12}>
          <Col span={16}>
            <Form.Item
              name="title"
              label="Naslov"
              rules={[{ required: true, message: "Unesite naslov" }]}
              style={{ marginBottom: 12 }}
            >
              <Input
                placeholder={
                  eventType === "blogger"
                    ? "npr. Ana Marković – lifestyle blog"
                    : "npr. Proljetna akcija -20%"
                }
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="sub" label="Podnaslov" style={{ marginBottom: 12 }}>
              <Input placeholder="opciono" />
            </Form.Item>
          </Col>
        </Row>

        {/* Apartman */}
        <Form.Item
          name="apartmentId"
          label="Apartman"
          style={{ marginBottom: 12 }}
        >
          <Select placeholder="Svi apartmani (ostavi prazno)" allowClear>
            {apartments.map((a) => (
              <Option key={a._id} value={a._id}>
                {a.name}
              </Option>
            ))}
          </Select>
        </Form.Item>

        {/* Period */}
        <Row gutter={12}>
          <Col span={10}>
            <Form.Item
              name="startDate"
              label="Datum od"
              style={{ marginBottom: 12 }}
            >
              <DatePicker style={{ width: "100%" }} format="DD.MM.YYYY" />
            </Form.Item>
          </Col>
          <Col span={10}>
            <Form.Item
              name="endDate"
              label="Datum do"
              style={{ marginBottom: 12 }}
            >
              <DatePicker style={{ width: "100%" }} format="DD.MM.YYYY" />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item name="nights" label="Noći" style={{ marginBottom: 12 }}>
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
        </Row>

        {/* Valuta */}
        <Form.Item label="Valuta" style={{ marginBottom: 12 }}>
          <div style={{ display: "flex", gap: 6 }}>
            {(["RSD", "EUR", "BAM"] as ApartmentCurrency[]).map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setCurrency(c)}
                style={{
                  height: 32,
                  padding: "0 14px",
                  borderRadius: 8,
                  border: `1.5px solid ${currency === c ? "#1a1520" : "#e8e5f0"}`,
                  background: currency === c ? "#1a1520" : "#fff",
                  color: currency === c ? "#fff" : "#6b6880",
                  fontWeight: 600,
                  fontSize: 12,
                  cursor: "pointer",
                  letterSpacing: "0.04em",
                  transition: "all 0.15s",
                }}
              >
                {c}
              </button>
            ))}
          </div>
        </Form.Item>

        {/* Cijena / troškovi */}
        <Row gutter={12}>
          <Col span={8}>
            <Form.Item
              name="cost"
              label={eventType === "blogger" ? "Cijena smještaja" : "Cijena / popust"}
              style={{ marginBottom: 12 }}
            >
              <InputNumber
                min={0}
                style={{ width: "100%" }}
                placeholder="0 = besplatno"
                formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ".")}
                parser={(v) => Number(v?.replace(/\./g, "")) as any}
                addonAfter={currency}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="extraCost"
              label="Ekstra troškovi"
              style={{ marginBottom: 12 }}
            >
              <InputNumber
                min={0}
                style={{ width: "100%" }}
                placeholder="0"
                formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ".")}
                parser={(v) => Number(v?.replace(/\./g, "")) as any}
                addonAfter={currency}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="guestPayment"
              label={
                <span>
                  Naknada gostu{" "}
                  <span
                    title="Iznos koji plaćate osobi za dolazak. Ne ulazi u statistiku prihoda."
                    style={{ color: "#ca8a04", fontWeight: 700, cursor: "help" }}
                  >
                    ↑ trošak
                  </span>
                </span>
              }
              style={{ marginBottom: 12 }}
            >
              <InputNumber
                min={0}
                style={{ width: "100%" }}
                placeholder="0"
                formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ".")}
                parser={(v) => Number(v?.replace(/\./g, "")) as any}
                addonAfter={currency}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Extras */}
        <Form.Item label="Extras / usluge" style={{ marginBottom: 4 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {extras.map((ex, i) => (
              <div
                key={i}
                style={{ display: "flex", gap: 8, alignItems: "center" }}
              >
                <Input
                  placeholder="Naziv (npr. Transfer aerodrom)"
                  value={ex.label}
                  onChange={(e) => updateExtra(i, "label", e.target.value)}
                  style={{ flex: 1 }}
                />
                <Select
                  value={ex.cls}
                  onChange={(v) => updateExtra(i, "cls", v)}
                  style={{ width: 100 }}
                >
                  <Option value="">Neutralno</Option>
                  <Option value="free">Besplatno</Option>
                  <Option value="paid">Plaćeno</Option>
                </Select>
                <Button
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => removeExtra(i)}
                />
              </div>
            ))}
            <Button
              size="small"
              type="dashed"
              icon={<PlusOutlined />}
              onClick={addExtra}
              style={{ alignSelf: "flex-start" }}
            >
              Dodaj extra
            </Button>
          </div>
        </Form.Item>

        {/* Deliverables / kontakt */}
        <Row gutter={12} style={{ marginTop: 12 }}>
          <Col span={12}>
            <Form.Item
              name="deliverables"
              label={eventType === "blogger" ? "Deliverables" : "Napomena"}
              style={{ marginBottom: 12 }}
            >
              <TextArea
                rows={2}
                placeholder={
                  eventType === "blogger"
                    ? "npr. 3 Instagram posta, 2 reels"
                    : "Detalji akcije"
                }
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="contact"
              label="Kontakt"
              style={{ marginBottom: 12 }}
            >
              <Input placeholder="Email, telefon ili Instagram" />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default AddEventModal;
